// First Created - 08 Oct 2026
// Author - Atul Pandey
// Purpose - Update an Org Trail Session Content Assignment

import React, {
    useEffect,
    useState,
} from "react"

import {
    useNavigate,
} from "react-router"

import useTitle from "@/hooks/useTitle"

import EtBtnFormBasic
    from "@/components/common/buttons/EtBtnFormBasic"

import PulseLoader
    from "react-spinners/PulseLoader"

import {
    useGetOrganizationsQuery,
} from "@/pages/admin/organizations/organizationsApiSlice"

import {
    useGetTrailsQuery,
} from "@/features/trails/trailsApiSlice"

import {
    useGetSessionsQuery,
} from "@/features/sessions/sessionsApiSlice"

import {
    useGetOrgTrailSessionCohortsQuery,
} from "@/pages/admin/orgTrailSessionCohorts/orgTrailSessionCohortsApiSlice"

import {
    useGetOrgTrailSessionContentAssignmentsQuery,
    useUpdateOrgTrailSessionContentAssignmentMutation,
} from "./orgTrailSessionContentAssignmentsApiSlice"


/*
|--------------------------------------------------------------------------
| 01 - Helpers
|--------------------------------------------------------------------------
*/

const getRefId = value =>
    String(value?._id || value?.id || value || "")

const toList = data =>
    data?.ids?.map(id => data.entities[id]).filter(Boolean) || []


/*
|--------------------------------------------------------------------------
| 02 - Main Component
|--------------------------------------------------------------------------
*/

const EtUpdateOrgTrailSessionContentAssignment = ({
    id,
    setActiveAdminView,
}) => {

    useTitle("Update Session Content Assignment")

    const {
        assignment,
        isLoading,
        isFetching,
        isError,
        error,
    } = useGetOrgTrailSessionContentAssignmentsQuery(
        {},
        {
            selectFromResult: ({
                data,
                isLoading,
                isFetching,
                isError,
                error,
            }) => ({
                assignment: data?.entities[id],
                isLoading,
                isFetching,
                isError,
                error,
            }),
        }
    )

    if (isLoading || isFetching) {
        return <PulseLoader color="#ea580c" />
    }

    if (isError) {
        return (
            <p className="text-sm text-orange-700">
                {error?.data?.message ||
                    "Unable to load assignment."}
            </p>
        )
    }

    if (!assignment) {
        return (
            <p className="text-sm text-gray-600 p-3">
                Assignment not found.
            </p>
        )
    }

    return (

        <EtUpdateOrgTrailSessionContentAssignmentForm
            key={id}
            assignment={assignment}
            id={id}
            setActiveAdminView={setActiveAdminView}
        />

    )
}


/*
|--------------------------------------------------------------------------
| 03 - Update Form
|--------------------------------------------------------------------------
*/

const EtUpdateOrgTrailSessionContentAssignmentForm = ({
    assignment,
    id,
    setActiveAdminView,
}) => {

    const navigate = useNavigate()

    const [
        active,
        setActive,
    ] = useState(
        assignment.active !== false
    )

    const [
        updateOrgTrailSessionContentAssignment,
        {
            isLoading,
            isSuccess,
            isError,
            error,
        },
    ] = useUpdateOrgTrailSessionContentAssignmentMutation()


    /*
    |--------------------------------------------------------------------------
    | 04 - Master Queries For Names
    |--------------------------------------------------------------------------
    */

    const {
        data: organizationsData,
    } = useGetOrganizationsQuery("organizationsList")

    const {
        data: trailsData,
    } = useGetTrailsQuery("trailsList")

    const {
        data: sessionsData,
    } = useGetSessionsQuery("sessionsList")

    const {
        data: cohortsData,
    } = useGetOrgTrailSessionCohortsQuery({})


    const resolveName = (data, ref, field) => {

        if (ref?.[field]) {
            return ref[field]
        }

        const item = toList(data).find(
            record => getRefId(record) === getRefId(ref)
        )

        return item?.[field] || "Not defined"
    }


    /*
    |--------------------------------------------------------------------------
    | 05 - Return To List
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (!isSuccess) return

        if (setActiveAdminView) {

            setActiveAdminView(
                "orgTrailSessionContentAssignments"
            )

        } else {

            navigate(
                "/orgTrailSessionContentAssignments"
            )

        }

    }, [
        isSuccess,
        navigate,
        setActiveAdminView,
    ])


    /*
    |--------------------------------------------------------------------------
    | 06 - Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async e => {

        e.preventDefault()

        try {

            await updateOrgTrailSessionContentAssignment({
                id,
                active,
            }).unwrap()

            alert(
                "Session Content assignment updated successfully"
            )

        } catch (submitError) {

            console.error(submitError)

            alert(
                submitError?.data?.message ||
                "Failed to update Session Content assignment"
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 07 - Cancel
    |--------------------------------------------------------------------------
    */

    const handleCancel = () => {

        if (setActiveAdminView) {

            setActiveAdminView(
                "orgTrailSessionContentAssignments"
            )

        } else {

            navigate(
                "/orgTrailSessionContentAssignments"
            )

        }
    }


    /*
    |--------------------------------------------------------------------------
    | 08 - Read Only Field
    |--------------------------------------------------------------------------
    */

    const ReadOnlyField = ({
        label,
        value,
    }) => (

        <div className="bg-white rounded m-1 p-2">

            <p className="font-bold text-xs m-1">
                {label}
            </p>

            <input
                type="text"
                value={value || "Not defined"}
                readOnly
                className="w-full rounded p-2 m-1 bg-gray-100 text-gray-600 cursor-not-allowed"
            />

        </div>
    )


    /*
    |--------------------------------------------------------------------------
    | 09 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">

            {isLoading && (
                <PulseLoader color="#ea580c" />
            )}

            {isError && (

                <p className="text-orange-700 py-2">
                    {error?.data?.message}
                </p>

            )}

            <div className="bg-white p-1 m-1 rounded">

                <p className="p-1 m-1 font-bold font-poppins text-xl">
                    Update Session Content Assignment
                </p>

                <div className="bg-gray-200 rounded m-1 p-1">

                    <form
                        onSubmit={handleSubmit}
                        className="m-1 p-1 text-xs font-poppins"
                    >

                        <ReadOnlyField
                            label="Organization"
                            value={resolveName(
                                organizationsData,
                                assignment.orgId,
                                "organizationName"
                            )}
                        />

                        <ReadOnlyField
                            label="Trail"
                            value={resolveName(
                                trailsData,
                                assignment.trailId,
                                "trailName"
                            )}
                        />

                        <ReadOnlyField
                            label="Cohort"
                            value={resolveName(
                                cohortsData,
                                assignment.cohortId,
                                "cohortName"
                            )}
                        />

                        <ReadOnlyField
                            label="Session"
                            value={resolveName(
                                sessionsData,
                                assignment.sessionId,
                                "sessionName"
                            )}
                        />

                        <ReadOnlyField
                            label="Reference Content"
                            value={
                                assignment.referenceContentId?.title ||
                                "Reference Content not defined"
                            }
                        />

                        <ReadOnlyField
                            label="Content Type"
                            value={
                                assignment.referenceContentId?.contentType ||
                                "Not defined"
                            }
                        />


                        {/* Active */}

                        <div className="bg-white rounded m-1 p-2">

                            <label className="flex items-center bg-gray-100 rounded m-1 p-2">

                                <input
                                    type="checkbox"
                                    checked={active}
                                    onChange={e =>
                                        setActive(e.target.checked)
                                    }
                                    className="m-1"
                                />

                                <span className="text-xs font-bold text-gray-700 m-1">
                                    Active Assignment
                                </span>

                            </label>

                        </div>


                        {/* Actions */}

                        <div className="bg-white m-1 px-1 py-3 rounded">

                            <EtBtnFormBasic
                                name="Update"
                                type="create"
                                handleClick={handleSubmit}
                            />

                            <EtBtnFormBasic
                                name="Cancel"
                                type="cancel"
                                handleClick={handleCancel}
                            />

                        </div>

                    </form>

                </div>

            </div>

        </div>

    )
}


export default EtUpdateOrgTrailSessionContentAssignment